<template>
  <iModal
    v-if="proposal"
    title="Review Collaboration Proposal"
    :close-on-secondary="false"
    :max-width="750"
    @close="onClose"
  >
    <template #body>
      <iColumn gap="large">
        <iColumn gap="small">
          <iRow align="left" wrap="nowrap" vertical-align="middle">
            <iColumn width="hug">
              <AvatarImage :src="proposal.publicationLogo" size="48px" />
            </iColumn>
            <iSubHeading>{{ proposal.publicationName }}</iSubHeading>
          </iRow>
          <iColumn gap="none">
            <iText variant="subtle">
              {{ proposal.publicationDomain }}
            </iText>
            <iText v-if="showInviteBy">
              Invited by: {{ proposal.owningOrgName }}
            </iText>
          </iColumn>
        </iColumn>

        <iColumn gap="none">
          <iSubHeading font-size="large">
            Role
          </iSubHeading>
          <iText>{{ camelToTitleCase(proposal.role) }}</iText>
        </iColumn>

        <iColumn gap="none">
          <iSubHeading font-size="large">
            Scope of Work
          </iSubHeading>
          <iText>{{ proposal.contractVersion.scope_of_work }}</iText>
        </iColumn>

        <iColumn gap="small">
          <iSubHeading font-size="large">
            Compensation
          </iSubHeading>

          <iColumn v-if="hasCompensation" gap="none">
            <iColumn v-if="Number(proposal.contractVersion.flat_fee) > 0" gap="small">
              <iRow vertical-align="middle" gap="small">
                <iText font-weight="dark">
                  Flat Fee:
                </iText>
                <iText>${{ proposal.contractVersion.flat_fee }}</iText>
                <iText>
                  {{ getFrequencyText(proposal.contractVersion.frequency) }}
                </iText>
              </iRow>
            </iColumn>

            <iColumn
              v-if="Number(proposal.contractVersion.percentage_revenue) > 0"
              gap="small"
            >
              <iRow vertical-align="middle" gap="small">
                <iText font-weight="dark">
                  Percentage of Revenue:
                </iText>
                <iText>
                  {{ proposal.contractVersion.percentage_revenue }}%
                </iText>
              </iRow>
            </iColumn>

            <iColumn
              v-if="Number(proposal.contractVersion.performance_based_revenue) > 0"
              gap="small"
            >
              <iRow vertical-align="middle" gap="small">
                <iText>
                  {{ getPerformanceCompensationText(proposal.contractVersion.performance_based_revenue, proposal.contractVersion.frequency) }}
                </iText>
              </iRow>
            </iColumn>
          </iColumn>

          <iText v-else variant="subtle">
            This proposal does not include compensation.
          </iText>
        </iColumn>
      </iColumn>

      <iColumn v-if="showCounteredMessage">
        <iSpace :height="15" />
        <iText variant="subtle">
          You have countered the proposal. Please wait for feedback from the
          other party before proceeding.
        </iText>
      </iColumn>
    </template>

    <template #footer>
      <iRow vertical-align="middle" align="right">
        <iColumn width="hug">
          <iButton variant="secondary" @click="onClose">
            Close
          </iButton>
        </iColumn>
        <iRow v-if="!showCounteredMessage" align="right" width="hug">
          <iColumn v-if="canRespond" width="hug">
            <iButton
              variant="secondary"
              :is-loading="isDenyingInvitation"
              loading-spinner-position="inside"
              @click="declineProposal"
            >
              Decline
            </iButton>
          </iColumn>
          <iColumn v-if="canCounter" width="hug">
            <iButton
              variant="secondary"
              :is-loading="isSubmittingCounterOffer"
              loading-spinner-position="inside"
              @click.stop="counterProposal"
            >
              Counter
            </iButton>
          </iColumn>
          <iColumn v-if="canRespond" width="hug">
            <iButton
              variant="primary"
              :is-loading="isAcceptingInvitation"
              loading-spinner-position="inside"
              @click="acceptProposal"
            >
              Accept
            </iButton>
          </iColumn>
        </iRow>
      </iRow>
    </template>
  </iModal>
</template>

<script>
import AvatarImage from "%/components/AvatarImage.vue";
import { camelToTitleCase } from "@bloglovin/vue-framework";
import { mapState } from "pinia";
import { useCollaboratorStore } from "@/stores/collaborator-store";
import { useGeneralStore } from "%/stores/general-store";

export default {
  name: "ProposalReviewModal",
  components: {
    AvatarImage,
  },
  props: {
    proposal: {
      type: Object,
      required: true,
    },
    showInviteBy: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  emits: ["close", "accept", "decline", "counter"],
  computed: {
    ...mapState(useCollaboratorStore, [
      "isAcceptingInvitation",
      "isDenyingInvitation",
      "isSubmittingCounterOffer",
    ]),
    ...mapState(useGeneralStore, ["orgId"]),
    canCounter() {
      if (this.proposal.contractVersion.org_id !== this.orgId) {
        return true;
      }
      return false;
    },
    showCounteredMessage() {
      return (
        this.proposal.contract &&
        this.proposal.contract.status === "countered" &&
        !this.canCounter
      );
    },
    canRespond() {
      return !this.showCounteredMessage;
    },
    hasCompensation() {
      const version = this.proposal.contractVersion;

      const compensationKeys = [
        "flat_fee",
        "percentage_revenue",
        "performance_based_revenue",
      ];

      return compensationKeys.some(key => Number(version[key]) > 0);
    },
  },
  methods: {
    camelToTitleCase,
    onClose() {
      this.$emit("close");
    },
    acceptProposal() {
      this.$emit("accept", this.proposal.publicationApplicantId);
    },
    declineProposal() {
      this.$emit("decline", this.proposal.publicationApplicantId);
    },
    counterProposal() {
      this.$emit("counter", { ...this.proposal });
    },
    getFrequencyText(frequency) {
      const frequencyMap = {
        per_month: "Per Month",
        per_week: "Per Week",
        per_deliverable: "Per Deliverable",
        per_story: "Per Story",
      };
      return frequencyMap[frequency] || frequency;
    },
    getPerformanceCompensationText(amount, frequency) {
      const numericAmount = Number(amount);
      if (!Number.isFinite(numericAmount) || numericAmount <= 1) {
        return "Performance Based Revenue Sharing";
      }

      return `Performance Bonus: $${numericAmount} ${this.getFrequencyText(frequency)}`;
    },
  },
};
</script>
